import { ReactNode } from 'react';
import { Container, Box, Button, Typography, Stack, Avatar, alpha, useTheme } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18nContext } from '@/i18n/i18n-react';

interface AdminPageHeaderProps {
    /** Icon to display in the avatar */
    icon: ReactNode;
    /** Main title of the page */
    title: string;
    /** Optional subtitle/description */
    subtitle?: string;
    /** Optional content to display below the title (e.g., chips, badges) */
    titleExtra?: ReactNode;
    /** Refetch buttons to display */
    refetchButton?: ReactNode;
    /** Action buttons to display on the right (e.g., Add button, Refresh button) */
    actions?: ReactNode;
    /** Optional custom content to display before actions (e.g., search field) */
    beforeActions?: ReactNode;
    /** Container max width, defaults to 'xl' */
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    /** Optional custom back navigation path, defaults to '/admin' */
    backPath?: string;
    /** Optional custom back button label */
    backLabel?: string;
}

/**
 * Standard header component for admin pages with consistent styling.
 * Includes a back button, icon, title, subtitle, and action buttons.
 */
export const AdminPageHeader = ({
    icon,
    title,
    subtitle,
    titleExtra,
    refetchButton,
    actions,
    beforeActions,
    maxWidth = 'xl',
    backPath = '/admin',
    backLabel,
}: AdminPageHeaderProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Container maxWidth={maxWidth} sx={{ py: { xs: 2, sm: 4 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                sx={{
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: { xs: 2, sm: 4 },
                }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(backPath)}
                        size="small"
                        sx={{
                            borderColor: 'divider',
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        {backLabel || LL.admin.backToAdmin()}
                    </Button>

                    {refetchButton}
                </Stack>

                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2 },
                        flex: 1,
                        minWidth: 0, // Allow shrinking below content size
                    }}
                >
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            flexShrink: 0, // Prevent avatar from shrinking
                        }}
                    >
                        {icon}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        {' '}
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2.125rem' }, // Smaller on mobile
                                lineHeight: { xs: 1.2, sm: 1.235 },
                                // Use a responsive clamp: single-line ellipsis on xs, up to 2 lines on sm+
                                display: { xs: 'block', sm: '-webkit-box' },
                                WebkitLineClamp: { xs: 1, sm: 2 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                                wordBreak: 'break-word', // Allow breaking long words when needed
                                overflowWrap: 'break-word',
                            }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontSize: { xs: '0.813rem', sm: '0.875rem' },
                                    mt: 0.25,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                        {titleExtra}
                    </Box>
                </Stack>

                <Stack
                    direction="row"
                    sx={{
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                >
                    {beforeActions}
                    {actions}
                </Stack>
            </Stack>
        </Container>
    );
};
